import { type Account } from '@/api/generated/model';
import { AccountForm } from '@/components/accounts/AccountForm';
import { AccountGroup } from '@/components/accounts/AccountGroup';
import { AccountPreview } from '@/components/accounts/AccountPreview';
import { AddAccountFlow } from '@/components/accounts/AddAccountFlow';
import { DeleteAccountDialog } from '@/components/accounts/DeleteAccountDialog';
import { BankingTabs } from '@/components/banking/BankingTabs';
import { PageFiltersMenu } from '@/components/layout/PageFiltersMenu';
import { PageHeaderAction } from '@/components/layout/PageHeaderAction';
import { PillarTabbedPage } from '@/components/layout/PillarTabbedPage';
import { useBankingPillarHero } from '@/components/layout/pillar-heroes/BankingPillarHero';
import { ErrorState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { useDetailPreview, type DetailPreview } from '@/contexts/DetailPreviewContext';
import { useAccountsPage } from '@/features/accounts';
import { useIsMobile } from '@/hooks/use-mobile';
import { useListPageWithPreview } from '@/hooks/useListPageWithPreview';
import { useLanguage } from '@/i18n/language-hooks';
import { FILTER_ALL } from '@/lib/filterConstants';
import { formatCurrency, useDefaultCurrency } from '@/lib/utils';
import { Plus, Wallet } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Accounts = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const defaultCurrency = useDefaultCurrency();
  const isMobile = useIsMobile();
  const { openPreview, closePreview } = useDetailPreview();
  const [isAsideFormOpen, setIsAsideFormOpen] = useState(false);

  const {
    accounts,
    groupedAccounts,
    totals,
    isLoading,
    error,
    setSelectedAccount,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    selectedAccountType,
    setSelectedAccountType,
    selectedCategory,
    setSelectedCategory,
    activeFiltersCount,
    clearAllFilters,
    handleAddAccount,
    handleEditAccount,
    handleDeleteAccount,
    handleRefreshBalance,
  } = useAccountsPage();

  const { netWorth } = totals;
  const hasRecords = !isLoading && accounts.length >= 1;

  const openEditAccountPanel = useCallback(
    (account: Account) => {
      if (isMobile) {
        void navigate(`/accounts/${account.id}/details?edit=1`);
        return;
      }
      setIsAsideFormOpen(true);
      openPreview({
        title: t('accounts.edit.title'),
        subtitle: t('accounts.edit.description'),
        content: (
          <AccountForm
            key={account.id}
            initialData={account}
            onSubmit={async (data) => {
              await handleEditAccount(data, account);
              setIsAsideFormOpen(false);
              closePreview();
            }}
            onCancel={() => {
              setIsAsideFormOpen(false);
              closePreview();
            }}
          />
        ),
        onClose: () => {
          setIsAsideFormOpen(false);
          closePreview();
        },
      });
    },
    [closePreview, handleEditAccount, isMobile, navigate, openPreview, t]
  );

  const openDeleteAccount = useCallback(
    (account: Account) => {
      setSelectedAccount(account);
      setIsDeleteDialogOpen(true);
    },
    [setIsDeleteDialogOpen, setSelectedAccount]
  );

  const buildAccountPreview = useCallback(
    (account: Account): DetailPreview => ({
      title: account.name,
      subtitle: account.institution || t(`accounts.types.${account.type}`),
      fullViewHref: `/accounts/${account.id}/details`,
      content: (
        <AccountPreview
          account={account}
          currency={defaultCurrency}
          onEdit={openEditAccountPanel}
          onRefreshBalance={handleRefreshBalance}
          onDelete={openDeleteAccount}
        />
      ),
    }),
    [defaultCurrency, handleRefreshBalance, openDeleteAccount, openEditAccountPanel, t]
  );

  const { selectedId, openItem, clearPreview } = useListPageWithPreview<Account>({
    items: accounts,
    isMobile,
    mobileDetailPath: (account) => `/accounts/${account.id}/details`,
    buildPreview: buildAccountPreview,
    enabled: !(!isMobile && isAsideFormOpen),
  });

  const filterMenu = useMemo(
    () => (
      <PageFiltersMenu activeCount={activeFiltersCount} onClear={clearAllFilters}>
        <div className='space-y-3'>
          <div className='space-y-2'>
            <Label htmlFor='filter-category' className='text-sm font-medium'>
              {t('accounts.filters.category')}
            </Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger id='filter-category'>
                <SelectValue placeholder={t('accounts.filters.categoryAll')} />
              </SelectTrigger>
              <SelectContent>
                {([FILTER_ALL, 'assets', 'liabilities'] as const).map((category) => (
                  <SelectItem key={category} value={category}>
                    {category === FILTER_ALL
                      ? t('accounts.filters.categoryAll')
                      : t(`accounts.filters.${category}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='filter-type' className='text-sm font-medium'>
              {t('accounts.filters.type.label')}
            </Label>
            <Select value={selectedAccountType} onValueChange={setSelectedAccountType}>
              <SelectTrigger id='filter-type'>
                <SelectValue placeholder={t('accounts.filters.type.all')} />
              </SelectTrigger>
              <SelectContent>
                {[
                  FILTER_ALL,
                  'bank',
                  'checking',
                  'savings',
                  'investment',
                  'retirement',
                  'real-estate',
                  'crypto',
                  'alternative',
                  'liability',
                ].map((type) => (
                  <SelectItem key={type} value={type}>
                    {type === FILTER_ALL
                      ? t('accounts.filters.type.all')
                      : t(`accounts.types.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PageFiltersMenu>
    ),
    [
      activeFiltersCount,
      clearAllFilters,
      selectedAccountType,
      selectedCategory,
      setSelectedAccountType,
      setSelectedCategory,
      t,
    ]
  );

  const openAddAccountPanel = useCallback(() => {
    if (isMobile) {
      void navigate('/banking/accounts/new');
      return;
    }
    clearPreview();
    setIsAsideFormOpen(true);
    openPreview({
      title: t('accounts.add.title'),
      subtitle: t('accounts.add.description'),
      fullViewHref: '/banking/accounts/new',
      size: 'md',
      content: (
        <AddAccountFlow
          variant='aside'
          onBeforeAutomatedNavigate={() => {
            setIsAsideFormOpen(false);
            closePreview();
          }}
          onSubmit={async (data) => {
            await handleAddAccount(data);
            setIsAsideFormOpen(false);
            closePreview();
          }}
          onCancel={() => {
            setIsAsideFormOpen(false);
            closePreview();
          }}
        />
      ),
      onClose: () => {
        setIsAsideFormOpen(false);
        closePreview();
      },
    });
  }, [clearPreview, closePreview, handleAddAccount, isMobile, navigate, openPreview, t]);

  const confirmDeleteAccount = useCallback(async () => {
    await handleDeleteAccount();
    clearPreview();
  }, [clearPreview, handleDeleteAccount]);

  const headerPrimaryAction = useMemo(
    () =>
      hasRecords ? (
        <PageHeaderAction
          icon={Plus}
          label={t('accounts.actions.add')}
          onClick={openAddAccountPanel}
        />
      ) : null,
    [hasRecords, openAddAccountPanel, t]
  );

  const bankingHero = useBankingPillarHero();

  return (
    <PillarTabbedPage
      breadcrumbItems={[
        {
          label: t('nav.banking'),
          href: '/banking/accounts',
          isActive: false,
        },
        {
          label: t('banking.tabs.accounts'),
          isActive: true,
        },
      ]}
      editMenuExtension={filterMenu}
      actions={headerPrimaryAction}
      hero={bankingHero}
      tabs={<BankingTabs active='accounts' />}
      trailing={
        <>
          <a id='top' className='sr-only' aria-hidden='true' />
          <DeleteAccountDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
            onConfirm={confirmDeleteAccount}
          />
        </>
      }
    >
      {error ? (
        <ErrorState
          title={t('accounts.error.title')}
          description={t('accounts.error.description')}
        />
      ) : (
        <section className='border-fin-gray-800/80 bg-card/50 flex min-h-0 min-w-0 flex-col rounded-2xl border p-3 sm:p-4'>
          <Link
            to='/overview/net-worth'
            className='bg-primary/5 text-foreground mb-4 grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_max-content] items-center gap-x-2 rounded-xl px-1 py-2 text-sm transition-colors hover:opacity-90'
          >
            <Wallet className='text-fin-green h-4 w-4 shrink-0' />
            <span className='min-w-0 truncate font-medium'>{t('accounts.netWorth')}</span>
            {isLoading ? (
              <Skeleton className='h-4 w-20 justify-self-end' />
            ) : (
              <span className='font-semibold whitespace-nowrap tabular-nums'>
                {formatCurrency(netWorth, defaultCurrency)}
              </span>
            )}
          </Link>

          <div className='min-w-0'>
            {isLoading ? (
              <div className='space-y-1'>
                {[...Array(3)].map((_, groupIdx) => (
                  <div key={groupIdx}>
                    <div className='flex items-center justify-between rounded-lg p-3'>
                      <div className='flex min-w-0 flex-1 items-center space-x-3'>
                        <div className='flex-shrink-0'>
                          <Skeleton className='h-8 w-8' />
                        </div>
                        <div className='min-w-0 flex-1'>
                          <Skeleton className='h-4 w-24' />
                        </div>
                      </div>
                      <Skeleton className='h-4 w-16' />
                    </div>
                  </div>
                ))}
              </div>
            ) : Object.entries(groupedAccounts).length > 0 ? (
              Object.entries(groupedAccounts).map(([type, typeAccounts]) => (
                <AccountGroup
                  key={type}
                  type={type}
                  accounts={typeAccounts}
                  onSelect={openItem}
                  selectedId={selectedId}
                />
              ))
            ) : (
              <div className='py-8 text-center'>
                <Wallet className='text-muted-foreground mx-auto mb-4 h-12 w-12 opacity-50' />
                <h3 className='mb-1 text-lg font-medium'>
                  {accounts.length === 0
                    ? t('accounts.noAccounts')
                    : t('accounts.noMatchingAccounts')}
                </h3>
                <p className='text-muted-foreground'>
                  {accounts.length === 0
                    ? t('accounts.noAccountsDescription')
                    : t('accounts.noMatchingAccountsDescription')}
                </p>
                {accounts.length === 0 && (
                  <Button className='mt-4' onClick={openAddAccountPanel}>
                    <Plus className='mr-2 h-4 w-4' /> {t('accounts.actions.add')}
                  </Button>
                )}
              </div>
            )}
          </div>

          {!hasRecords && (
            <Button onClick={openAddAccountPanel} className='mt-4 w-full'>
              <Plus className='mr-2 h-4 w-4' />
              {t('accounts.actions.add')}
            </Button>
          )}
        </section>
      )}
    </PillarTabbedPage>
  );
};

export default Accounts;
